<template>
  <div class="px-2 py-4">
    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center">
        <Megaphone class="w-5 h-5 mx-2 text-gray-600" />
        <h1 class="text-gray-900">Warnings & Reports</h1>
        <span class="ml-2 text-sm text-gray-500">[{{ reportsStore.totalCount }}]</span>
      </div>
      <div class="flex items-center gap-4">
        <!-- Filter Buttons -->
        <div class="flex items-center gap-2">
          <button 
            class="text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="reportsStore.filters.type === 'all' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="reportsStore.setFilter('all')"
          >
            All
          </button>
          <button 
            class="flex items-center text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="reportsStore.filters.type === 'reports' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="reportsStore.setFilter('reports')"
          >
            <span class="w-1.5 h-1.5 bg-amber-500 mr-1.5 rounded-full"></span>
            Reports
          </button>
          <button 
            class="flex items-center text-xs px-3 py-1 rounded-xl border-[1px] border-gray-200 transition-all duration-200"
            :class="reportsStore.filters.type === 'strikes' ? 'text-gray-700 font-medium' : ' text-gray-400 hover:bg-gray-200'"
            @click="reportsStore.setFilter('strikes')"
          >
          <span class="w-1.5 h-1.5 bg-red-500 mr-1.5 rounded-full"></span>
            Warnings
          </button>
        </div>

        <!-- Search Bar -->
        <div class="relative w-[300px]">
          <Search class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search reports or warnings..."
            class="w-full pl-9 pr-4 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            v-model="searchQuery"
            @input="handleSearch"
          />
        </div>

        <button 
          class="text-xs flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-400 transition-all duration-200"
          @click="isStrikeModalOpen = true"
        >
          <Plus class="w-4 h-4" />
          Issue Warning
        </button>
      </div>
    </div>

    <div v-if="reportsStore.loading" class="flex justify-center items-center py-8 min-h-[calc(100vh-4rem)]">
      <OuttalentLoader full-title />
    </div>

    <div v-else-if="reportsStore.error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
      <p class="text-red-600">{{ reportsStore.error }}</p>
    </div>

    <div v-else>
      <!-- Reports Table -->
      <div class="bg-white">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Type
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  User
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Count
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Reason
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200"
                >
                  Status
                </th>
                <th
                  class="text-left py-3 px-4 font-normal text-xs text-gray-700 border border-gray-200 w-32"
                >
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="report in reports" 
                :key="report.id" 
                class="hover:bg-gray-50 cursor-pointer"
                @click="selectedReport = report"
              >
                <!-- Type Column -->
                <td class="py-3 px-1 border border-gray-100 text-center">
                  <span
                    v-if="report.is_strike && report.resulted_in_suspension"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-black text-white"
                  >
                    Suspension
                  </span>
                  <span
                    v-else-if="report.is_strike && !report.is_revoked_strike"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                  >
                    Warning
                  </span>

                  <span
                    v-else-if="report.is_revoked_strike"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    Revoked
                  </span>
                  <span
                    v-else
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                  >
                    Report
                  </span>
                </td>

                <!-- User Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div class="flex items-center gap-3">
                    <FellowAvatarDropdown
                      :user-id="report.user_details.id"
                      :username="report.user_details.username"
                      :avatar-src="report.user_details?.avatar_image"
                      :name="`${report.user_details.first_name} ${report.user_details.last_name}`"
                      :curriculum-path="`/manage/campus/fellows/${report.user_details.id}`"
                      :is-blurred="isBlurred"
                      size="md"
                      @click.stop
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900" :class="{ 'blur-md select-none': isBlurred }">
                        {{ report.user_details.first_name }} {{ report.user_details.last_name }}
                      </div>
                      <div class="text-xs text-gray-400">{{ report.fellow_data?.batch ? formatExpeditionName(report.fellow_data.batch) : 'No batch assigned' }}</div>
                    </div>
                  </div>
                </td>

                <!-- Count Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div class="flex items-center justify-center gap-1">
                    <Flag
                      v-for="n in report.strike_count"
                      :key="n"
                      class="w-4 h-4 text-yellow-500"
                    />
                  </div>
                </td>

                <!-- Reason Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <div>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <component
                        :is="reportHelpers.getReasonIcon(report.reason)"
                        class="w-3 h-3 mr-1 text-red-500"
                      />

                      {{ report.reason_display }}
                    </span>
                    <div
                      v-if="report.description"
                      class="text-xs text-gray-500 mt-1 max-w-xs truncate"
                      :title="report.description"
                    >
                      {{ report.description }}
                    </div>
                  </div>
                </td>

                <!-- Status Column -->
                <td class="py-3 px-4 border border-gray-100">
                  <span
                    :class="reportHelpers.getStatusClass(report.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs"
                  >
                    {{ report.status_display }}
                  </span>
                </td>

                <!-- Date Column -->
                <td class="py-3 px-4 border border-gray-100 w-32">
                  <div class="text-xs text-gray-900">{{ formatDateUtil(report.created_at) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="!reportsStore.hasReports" class="text-center py-12">
          <div class="text-gray-500">No reports found</div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="reportsStore.isPaginated" class="mt-6">
        <PaginationComponent
          :current-page="pagination.currentPage.value"
          :total-pages="reportsStore.pagination.totalPages"
          :use-router="true"
          @update:page="pagination.updatePage"
          class="mb-0"
        />
      </div>
    </div>

    <!-- Report Details Modal -->
    <ReportDetailsModal
      v-if="selectedReport"
      :selected-report="selectedReport"
      @close="selectedReport = null"
      @navigate-to-report="navigateToReport"
      @report-updated="handleReportUpdate"
    />

    <!-- Issue Warning Modal -->
    <IssueStrikeModal
      v-if="isStrikeModalOpen"
      :is-open="isStrikeModalOpen"
      @close="isStrikeModalOpen = false"
      @submitted="handleStrikeSubmitted"
    />
  </div>
</template>

<script setup lang="ts">
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import { Flag, Megaphone, Plus, Search } from 'lucide-vue-next'
import FellowAvatarDropdown from '@/components/common/FellowAvatarDropdown.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useReportsStore } from '@/stores/reports'
import type { Report, FellowReport } from '@/types/reports'
import { useRouterPagination } from '@/composables/useRouterPagination'
import { useReportHelpers } from '@/composables/useReportHelpers'
import { useDebounce } from '@/composables/useDebounce'
import ReportDetailsModal from '@/components/manage/ReportDetailsModal.vue'
import IssueStrikeModal from '@/components/manage/IssueStrikeModal.vue'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import { ref, computed } from 'vue'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const { isBlurred } = usePrivacyMode()

const reportsStore = useReportsStore()
const reportHelpers = useReportHelpers()
const selectedReport = ref<Report | null>(null)
const isStrikeModalOpen = ref(false)
const { debounce } = useDebounce(300) // Add debounce composable

const navigateToReport = (report: Report | FellowReport) => {
  selectedReport.value = report as Report
}

const handleReportUpdate = (updatedReport: Report | FellowReport) => {
  const report = updatedReport as Report
  const index = reportsStore.reports.findIndex(r => r.id === report.id)
  if (index !== -1) {
    reportsStore.reports[index] = report
  }
  
  if (selectedReport.value?.id === report.id) {
    selectedReport.value = report
  }
}

const handleStrikeSubmitted = () => {
  // Refresh the reports list after a strike is submitted
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  reportsStore.fetchReports({ page: pagination.currentPage.value })
}

const pagination = useRouterPagination(
  async (page: number) => {
    await reportsStore.fetchReports({ page })
  }
)

// Type assertion to ensure store reports match our interface
const reports = computed(() => reportsStore.reports as Report[])

const searchQuery = ref('')

// Use the composable's debounce function
const handleSearch = debounce(() => {
  reportsStore.setSearch(searchQuery.value)
})
</script>
